import { FC, useCallback, useMemo, useState } from 'react';
import { GridCell, GridCellKind, Item } from '@glideapps/glide-data-grid';
import { useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useFinancialValueDisplay } from '@/shared/components/financial-values';
import FinancialGlideDataGrid from '@/shared/components/tables/FinancialGlideDataGrid';
import { getPlanHistoricalDataYears } from '@/shared/pricing';
import { useUserStore } from '@/stores';

import { isDividendHistoryRowLocked, normalizeDividendHistoryRows } from '../../financialSeries';
import { createDividendHistoryGridCell, createDividendHistoryGridColumns, sortDividendHistoryRows } from './gridModel';
import type { DividendHistorySort } from './gridModel';

interface DividendHistoryTableProps {
    data: Array<Record<string, unknown>>;
}

const DividendHistoryTable: FC<DividendHistoryTableProps> = ({ data }): JSX.Element => {
    const { token } = useAntDesignTheme();
    const { preference: financialValueDisplay } = useFinancialValueDisplay();
    const navigate = useNavigate();
    const { selectedUser: user } = useUserStore();
    const historicalDataYears = getPlanHistoricalDataYears(user?.plan);
    const [sort, setSort] = useState<DividendHistorySort>({ columnId: 'exDividendDate', direction: 'desc' });
    const normalizedRows = useMemo(() => normalizeDividendHistoryRows(data), [data]);
    const rows = useMemo(() => sortDividendHistoryRows(normalizedRows, sort), [normalizedRows, sort]);
    const columns = useMemo(() => createDividendHistoryGridColumns(sort), [sort]);

    const getCellContent = useCallback(
        ([columnIndex, rowIndex]: Item): GridCell => {
            const column = columns[columnIndex];
            const row = rows[rowIndex];

            if (!column || !row) {
                return {
                    allowOverlay: false,
                    data: '',
                    displayData: '',
                    kind: GridCellKind.Text,
                    readonly: true,
                };
            }

            return createDividendHistoryGridCell(
                row,
                column.id,
                isDividendHistoryRowLocked(row, historicalDataYears),
                {
                    negative: token.colorError,
                    positive: token.colorSuccess,
                },
                financialValueDisplay,
            );
        },
        [columns, financialValueDisplay, historicalDataYears, rows, token.colorError, token.colorSuccess],
    );

    const handleHeaderClicked = useCallback(
        (columnIndex: number) => {
            const columnId = columns[columnIndex]?.id;

            if (!columnId) {
                return;
            }

            setSort((current) => ({
                columnId,
                direction: current.columnId === columnId && current.direction === 'asc' ? 'desc' : 'asc',
            }));
        },
        [columns],
    );

    const handleCellClicked = useCallback(
        ([columnIndex, rowIndex]: Item) => {
            const column = columns[columnIndex];
            const row = rows[rowIndex];

            if (column?.id !== 'exDividendDate' && row && isDividendHistoryRowLocked(row, historicalDataYears)) {
                navigate('/settings/account/plans/billing');
            }
        },
        [columns, historicalDataYears, navigate, rows],
    );

    return (
        <FinancialGlideDataGrid
            columns={columns}
            freezeColumns={1}
            getCellContent={getCellContent}
            getCellsForSelection={true}
            onCellClicked={handleCellClicked}
            onHeaderClicked={handleHeaderClicked}
            rows={rows.length}
        />
    );
};

export default DividendHistoryTable;
